import React, { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Loader2, Phone, Wrench, Snowflake, ShieldCheck, Truck, Clock, Bot } from "lucide-react";
import svcWorkshop from "@/assets/svc-team-3.png";
import lubaHero from "@/assets/luba-hero.webp";

const POINTS = [
  { icon: Wrench, title: "Full workshop service", copy: "Strip-down, deep clean, diagnostics, blade & wear checks, rebuild and full testing." },
  { icon: Snowflake, title: "Winter storage", copy: "We collect, service and securely store your mower off-site over winter." },
  { icon: ShieldCheck, title: "All major brands", copy: "Mammotion, Roborock, Stiga, Husqvarna and more — genuine parts used." },
  { icon: Truck, title: "Collection & return", copy: "DPD collection or our own collection service, then delivered back ready to mow." },
];

const STEPS = [
  "Tell us your mower model and the problem (or that you want winter storage).",
  "We arrange collection or a drop-off to our Co. Meath workshop.",
  "Full service, diagnostics and any approved repairs — no surprise charges.",
  "Secure winter storage, then pre-season prep and return to your door.",
];

export default function RepairsWinter() {
  const site = useSite();
  useEffect(() => {
    api.post("/analytics/track", { path: "/lp/repairs-winter-storage" }).catch(() => {});
  }, []);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", county: "", message: "", company: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) { toast.error("Please enter your name, email and phone."); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/enquiries", { ...form, type: "service", source_page: "lp/repairs-winter-storage" });
      setDone(true);
      toast.success(res.data.message);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  const tel = `tel:${site.phone?.replace(/\s/g, "")}`;

  return (
    <div className="min-h-screen bg-brand-white">
      {/* Minimal header (no site nav) */}
      <header className="bg-brand-navy text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2"><span className="grid place-items-center h-9 w-9 rounded-lg bg-white text-brand-navy"><Bot className="h-5 w-5" /></span><span className="font-display font-extrabold text-sm sm:text-base">Robotic Lawnmower Experts</span></div>
          <a href={tel} className="flex items-center gap-2 font-semibold text-white" data-testid="lp-header-phone"><Phone className="h-4 w-4" /> {site.phone}</a>
        </div>
      </header>

      {/* Hero + form */}
      <section className="relative bg-brand-ink text-white">
        <div className="absolute inset-0"><img src={lubaHero} alt="" className="h-full w-full object-cover opacity-25" /><div className="absolute inset-0 bg-gradient-to-r from-brand-ink to-brand-ink/70" /></div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 font-mono-spec text-xs uppercase tracking-widest text-white"><Clock className="h-4 w-4" /> Ireland-wide collection</span>
            <h1 className="mt-4 font-display font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.05]">Robotic Mower Repairs &amp; Winter Storage</h1>
            <p className="mt-5 text-lg text-white/85">Expert servicing and repairs for all major robotic mower brands — plus secure winter storage so your mower is ready to go next season. Based in Kells, Co. Meath, serving all of Ireland.</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" className="h-12 px-7 bg-white hover:bg-white/90 text-brand-navy font-semibold"><a href="#lp-form" data-testid="lp-hero-cta">Get a free quote</a></Button>
              <Button asChild size="lg" variant="outline" className="h-12 px-7 border-white/30 bg-white/5 text-white hover:bg-white hover:text-brand-navy"><a href={tel}><Phone className="mr-2 h-5 w-5" /> {site.phone}</a></Button>
            </div>
          </div>

          {/* Form card */}
          <div id="lp-form" className="rounded-3xl bg-white text-brand-ink p-6 md:p-8 shadow-2xl">
            {done ? (
              <div className="py-8 text-center" data-testid="lp-success">
                <CheckCircle2 className="mx-auto h-14 w-14 text-brand-navy" />
                <h2 className="mt-4 font-display font-bold text-2xl">Request received</h2>
                <p className="mt-2 text-muted-foreground">Your enquiry has been received. Our team will contact you to discuss your requirements.</p>
              </div>
            ) : (
              <form onSubmit={submit}>
                <h2 className="font-display font-bold text-2xl text-brand-navy">Book a service or storage</h2>
                <p className="mt-1 text-sm text-muted-foreground">Quick quote — we'll call you back.</p>
                <input type="text" value={form.company} onChange={(e) => set("company", e.target.value)} className="hidden" tabIndex={-1} aria-hidden />
                <div className="mt-5 grid gap-4">
                  <div><Label htmlFor="lp-name">Name *</Label><Input id="lp-name" data-testid="lp-name" value={form.name} onChange={(e) => set("name", e.target.value)} required /></div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div><Label htmlFor="lp-email">Email *</Label><Input id="lp-email" type="email" data-testid="lp-email" value={form.email} onChange={(e) => set("email", e.target.value)} required /></div>
                    <div><Label htmlFor="lp-phone">Phone *</Label><Input id="lp-phone" data-testid="lp-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} required /></div>
                  </div>
                  <div><Label htmlFor="lp-county">County</Label><Input id="lp-county" data-testid="lp-county" value={form.county} onChange={(e) => set("county", e.target.value)} /></div>
                  <div><Label htmlFor="lp-message">Mower model & issue</Label><Textarea id="lp-message" rows={3} data-testid="lp-message" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="e.g. Mammotion LUBA — won't charge / winter storage" /></div>
                  <Button type="submit" disabled={submitting} size="lg" className="w-full bg-brand-navy hover:bg-brand-navy/90 text-white font-semibold" data-testid="lp-submit">
                    {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</> : "Request a free quote"}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Points */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {POINTS.map((p) => (
            <div key={p.title} className="rounded-2xl border border-brand-navy/10 bg-white p-6">
              <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand-navy text-white"><p.icon className="h-5 w-5" /></span>
              <h3 className="mt-4 font-display font-semibold text-lg text-brand-navy">{p.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{p.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works + image */}
      <section className="bg-white border-y border-brand-navy/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-3xl overflow-hidden shadow-xl ring-1 ring-brand-navy/10"><img src={svcWorkshop} alt="Robotic mower being serviced in the workshop" className="w-full h-80 object-cover" /></div>
          <div>
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl text-brand-ink">How it works</h2>
            <ol className="mt-6 space-y-4">
              {STEPS.map((s, i) => (
                <li key={i} className="flex gap-4"><span className="grid place-items-center h-8 w-8 shrink-0 rounded-full bg-brand-navy text-white font-mono-spec text-sm">{i + 1}</span><span className="text-brand-ink pt-1">{s}</span></li>
              ))}
            </ol>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" className="h-12 px-7 bg-brand-navy hover:bg-brand-navy/90 text-white font-semibold"><a href="#lp-form">Get my free quote</a></Button>
              <Button asChild size="lg" variant="outline" className="h-12 px-7 border-brand-navy/30 text-brand-navy hover:bg-brand-navy hover:text-white"><a href={tel}><Phone className="mr-2 h-5 w-5" /> Call {site.phone}</a></Button>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-brand-ink text-white/70">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-sm flex flex-col sm:flex-row items-center justify-between gap-3">
          <p>© {new Date().getFullYear()} Robotic Lawnmower Experts — {site.address}</p>
          <a href={tel} className="font-semibold text-white flex items-center gap-2"><Phone className="h-4 w-4" /> {site.phone}</a>
        </div>
      </footer>
    </div>
  );
}
